/**
 * Screen-space reflections (WebGPU / TSL).
 */

import { ssr } from 'three/addons/tsl/display/SSRNode.js';
import { colorToDirection, sample } from 'three/tsl';

import { type EffectContext, type WebGPUEffectNode } from '../IPostProcessEffect.js';
import { DefaultPostprocessEffectConfigs, PostProcessPass, PostProcessRenderOrder, type SSREffectConfig } from '../PostProcessConfig.js';

import { WebGPUOnlyEffect } from './WebGPUOnlyEffectBase.js';

type SSRRuntimeNode = WebGPUEffectNode & {
  ssrRuntime: {
    quality: { value: number };
    blurQuality: { value: number };
    maxDistance: { value: number };
    opacity: { value: number };
    thickness: { value: number };
    resolutionScale: { value: number };
  };
  ssrNode?: { resolutionScale: number };
};

function clampSSRQuality(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function clampSSRBlurQuality(value: number): number {
  return Math.max(1, Math.min(3, Math.round(value)));
}

function clampSSRResolutionScale(value: number): number {
  return Math.max(0.1, Math.min(1, value));
}

export class SSREffect extends WebGPUOnlyEffect<SSREffectConfig> {
  override readonly type = PostProcessPass.SSR;
  override readonly order = PostProcessRenderOrder.SSR;

  override getDefaultConfig(): SSREffectConfig {
    return { ...DefaultPostprocessEffectConfigs[PostProcessPass.SSR] };
  }

  override createWebGPUNode(config: SSREffectConfig, _context: EffectContext): WebGPUEffectNode {
    const defaults = this.getDefaultConfig();
    const runtimeNode: SSRRuntimeNode = {
      node: null as any,
      mrtOutputs: ['normal', 'metalrough'],
      needsMRT: true,
      ssrRuntime: {
        quality: { value: clampSSRQuality(config.quality ?? defaults.quality ?? 0.5) },
        blurQuality: { value: clampSSRBlurQuality(config.blurQuality ?? defaults.blurQuality ?? 1) },
        maxDistance: { value: Math.max(0, config.maxDistance ?? defaults.maxDistance ?? 1) },
        opacity: { value: Math.max(0, config.opacity ?? defaults.opacity ?? 1) },
        thickness: { value: Math.max(0, config.thickness ?? defaults.thickness ?? 0.03) },
        resolutionScale: { value: clampSSRResolutionScale(config.resolutionScale ?? defaults.resolutionScale ?? 1) },
      },
      compose: (outputNode, ctx) => {
        const metalRoughNode = ctx.getMRTTexture('metalrough');
        const normalTextureNode = ctx.getMRTTexture('normal');
        if (!metalRoughNode || !normalTextureNode) return outputNode;
        // SSR expects a sample-able colour texture node, not an already-composed vec4 expression.
        const sceneColorNode = ctx.scenePass.getTextureNode('output');
        const sceneNormalNode = sample((uv: any) => colorToDirection((normalTextureNode as any).sample(uv)));
        const ssrNode = ssr(
          sceneColorNode as any,
          ctx.depthNode as any,
          sceneNormalNode as any,
          (metalRoughNode as any).r,
          (metalRoughNode as any).g,
          ctx.camera as any
        ) as any;
        const qualityValue = runtimeNode.ssrRuntime.quality.value;
        const blurQualityValue = runtimeNode.ssrRuntime.blurQuality.value;
        const maxDistanceValue = runtimeNode.ssrRuntime.maxDistance.value;
        const opacityValue = runtimeNode.ssrRuntime.opacity.value;
        const thicknessValue = runtimeNode.ssrRuntime.thickness.value;
        const resolutionScaleValue = runtimeNode.ssrRuntime.resolutionScale.value;

        runtimeNode.ssrRuntime.quality = ssrNode.quality;
        runtimeNode.ssrRuntime.blurQuality = ssrNode.blurQuality;
        runtimeNode.ssrRuntime.maxDistance = ssrNode.maxDistance;
        runtimeNode.ssrRuntime.opacity = ssrNode.opacity;
        runtimeNode.ssrRuntime.thickness = ssrNode.thickness;

        runtimeNode.ssrRuntime.quality.value = qualityValue;
        runtimeNode.ssrRuntime.blurQuality.value = blurQualityValue;
        runtimeNode.ssrRuntime.maxDistance.value = maxDistanceValue;
        runtimeNode.ssrRuntime.opacity.value = opacityValue;
        runtimeNode.ssrRuntime.thickness.value = thicknessValue;
        ssrNode.resolutionScale = resolutionScaleValue;
        runtimeNode.ssrNode = ssrNode;

        // SSR node outputs reflection contribution (premultiplied), blend additively over current pipeline output.
        return (outputNode as any).add(ssrNode.rgb) as any;
      },
    };

    return runtimeNode;
  }

  override applyWebGPUConfig(node: WebGPUEffectNode, config: SSREffectConfig): void {
    const runtimeNode = node as SSRRuntimeNode;
    if (!runtimeNode.ssrRuntime) return;

    const defaults = this.getDefaultConfig();
    runtimeNode.ssrRuntime.quality.value = clampSSRQuality(config.quality ?? defaults.quality ?? 0.5);
    runtimeNode.ssrRuntime.blurQuality.value = clampSSRBlurQuality(config.blurQuality ?? defaults.blurQuality ?? 1);
    runtimeNode.ssrRuntime.maxDistance.value = Math.max(0, config.maxDistance ?? defaults.maxDistance ?? 1);
    runtimeNode.ssrRuntime.opacity.value = Math.max(0, config.opacity ?? defaults.opacity ?? 1);
    runtimeNode.ssrRuntime.thickness.value = Math.max(0, config.thickness ?? defaults.thickness ?? 0.03);
    const resolutionScale = clampSSRResolutionScale(config.resolutionScale ?? defaults.resolutionScale ?? 1);
    runtimeNode.ssrRuntime.resolutionScale.value = resolutionScale;
    if (runtimeNode.ssrNode) {
      runtimeNode.ssrNode.resolutionScale = resolutionScale;
    }
  }

  override requiresRebuild(oldConfig: SSREffectConfig, newConfig: SSREffectConfig): boolean {
    return oldConfig.enabled !== newConfig.enabled;
  }
}
